import { create } from 'zustand';
import { ISSUE, ISSUE_TARGET, configIssue, issueMessage } from '../lib/rcu/catalog/issue';
import type {
  DaliBridgeDraft,
  DaliBridgePatch,
} from '../lib/rcu/commands/daliBridges';
import { addDaliBridge, removeDaliBridge, updateDaliBridge } from '../lib/rcu/commands/daliBridges';
import type { BindOptions, LoadDraft, LoadPatch } from '../lib/rcu/commands/loads';
import {
  addLoad,
  bindLoad,
  moveLoadToRoom,
  removeLoad,
  unbindLoad,
  updateLoad,
} from '../lib/rcu/commands/loads';
import type { RailDraft, RailPatch } from '../lib/rcu/commands/rails';
import { addRail, removeRail, updateRail } from '../lib/rcu/commands/rails';
import type { RcuIdentityPatch } from '../lib/rcu/commands/rcu';
import { setOutputCount, updateIdentity } from '../lib/rcu/commands/rcu';
import type { RoomDraft, RoomPatch } from '../lib/rcu/commands/rooms';
import {
  addRoom,
  assignLoadsToRoom,
  removeRoom,
  setDefaultRoom,
  updateRoom,
} from '../lib/rcu/commands/rooms';
import { decodeConfig } from '../lib/rcu/decode';
import type { ChannelRef } from '../lib/rcu/catalog/channel';
import type { DaliBridgeId, LoadId, RailId, RoomId } from '../lib/rcu/ids';
import type { CommandResult, Load, RcuConfig, RcuIdentity } from '../lib/rcu/model';
import { createEmptyConfig } from '../lib/rcu/model';
import { clearStoredConfig, loadStoredConfig, saveStoredConfig } from '../lib/rcu/persistence';
import { loadsInRoom } from '../lib/rcu/query/grouping';
import { fail, ok } from '../lib/rcu/result';
import type { Result } from '../lib/rcu/result';
import { assertConfigInvariants, errorsOf, validateConfig } from '../lib/rcu/validation/validate';

/**
 * Single source of truth for the RCU definition.
 *
 * Every action delegates to a pure command and only commits when the command
 * succeeded, so the store can never hold a half-applied change. Actions return
 * the command's `Result` so callers can surface the reason for a refusal.
 */
export interface RcuConfigStore {
  readonly config: RcuConfig;

  updateIdentity: (patch: RcuIdentityPatch) => Result<RcuIdentity>;
  setOutputCount: (outputCount: number) => Result<number>;

  addRail: (draft: RailDraft) => Result<RailId>;
  updateRail: (railId: RailId, patch: RailPatch) => Result<RailId>;
  removeRail: (railId: RailId) => Result<number>;

  addDaliBridge: (draft: DaliBridgeDraft) => Result<DaliBridgeId>;
  updateDaliBridge: (bridgeId: DaliBridgeId, patch: DaliBridgePatch) => Result<DaliBridgeId>;
  removeDaliBridge: (bridgeId: DaliBridgeId) => Result<number>;

  addLoad: (draft: LoadDraft) => Result<LoadId>;
  updateLoad: (loadId: LoadId, patch: LoadPatch) => Result<LoadId>;
  removeLoad: (loadId: LoadId) => Result<LoadId>;
  bindLoad: (loadId: LoadId, ref: ChannelRef, options?: BindOptions) => Result<Load | null>;
  unbindLoad: (loadId: LoadId) => Result<LoadId>;
  moveLoadToRoom: (loadId: LoadId, roomId: RoomId | null) => Result<LoadId>;

  addRoom: (draft: RoomDraft) => Result<RoomId>;
  updateRoom: (roomId: RoomId, patch: RoomPatch) => Result<RoomId>;
  removeRoom: (roomId: RoomId) => Result<number>;
  assignLoadsToRoom: (loadIds: readonly LoadId[], roomId: RoomId | null) => Result<number>;
  /** Empties a room without deleting it. */
  unassignRoom: (roomId: RoomId) => Result<number>;
  setDefaultRoom: (roomId: RoomId | null) => Result<RoomId | null>;

  importConfig: (value: unknown) => Result<RcuConfig>;
  /**
   * Commits a configuration that came from outside the command layer — an
   * imported file. Refuses one the console itself would report errors about,
   * rather than storing a configuration the console then refuses to leave.
   */
  applyConfig: (config: RcuConfig) => Result<RcuConfig>;
  resetConfig: () => void;
}

/** `now` is read once per action, so a whole command shares one timestamp. */
const nowIso = (): string => new Date().toISOString();

/**
 * An issue's sentence without its full stop, so it can be embedded in another.
 *
 * The issue catalog writes complete sentences because that is what a list needs;
 * this is the one place one is quoted inside a larger sentence.
 */
const stripPeriod = (sentence: string): string => sentence.replace(/\.$/, '');

const initialConfig = (): RcuConfig => loadStoredConfig(nowIso());

export const useRcuConfigStore = create<RcuConfigStore>((set, get) => {
  /** Runs a command, commits on success, persists, and returns what happened. */
  const run = <T>(command: (config: RcuConfig, now: string) => CommandResult<T>): Result<T> => {
    const now = nowIso();
    const result = command(get().config, now);
    if (!result.ok) return fail(result.issue);

    const { config, value } = result.value;
    if (import.meta.env.DEV) assertConfigInvariants(config);
    set({ config });
    saveStoredConfig(config);
    return ok(value);
  };

  return {
    config: initialConfig(),

    updateIdentity: (patch) => run((config, now) => updateIdentity(config, patch, now)),
    setOutputCount: (outputCount) => run((config, now) => setOutputCount(config, outputCount, now)),

    addRail: (draft) => run((config, now) => addRail(config, draft, now)),
    updateRail: (railId, patch) => run((config, now) => updateRail(config, railId, patch, now)),
    removeRail: (railId) => run((config, now) => removeRail(config, railId, now)),

    addDaliBridge: (draft) => run((config, now) => addDaliBridge(config, draft, now)),
    updateDaliBridge: (bridgeId, patch) =>
      run((config, now) => updateDaliBridge(config, bridgeId, patch, now)),
    removeDaliBridge: (bridgeId) => run((config, now) => removeDaliBridge(config, bridgeId, now)),

    addLoad: (draft) => run((config, now) => addLoad(config, draft, now)),
    updateLoad: (loadId, patch) => run((config, now) => updateLoad(config, loadId, patch, now)),
    removeLoad: (loadId) => run((config, now) => removeLoad(config, loadId, now)),
    bindLoad: (loadId, ref, options) =>
      run((config, now) => bindLoad(config, loadId, ref, options ?? {}, now)),
    unbindLoad: (loadId) => run((config, now) => unbindLoad(config, loadId, now)),
    moveLoadToRoom: (loadId, roomId) =>
      run((config, now) => moveLoadToRoom(config, loadId, roomId, now)),

    addRoom: (draft) => run((config, now) => addRoom(config, draft, now)),
    updateRoom: (roomId, patch) => run((config, now) => updateRoom(config, roomId, patch, now)),
    removeRoom: (roomId) => run((config, now) => removeRoom(config, roomId, now)),
    assignLoadsToRoom: (loadIds, roomId) =>
      run((config, now) => assignLoadsToRoom(config, loadIds, roomId, now)),
    unassignRoom: (roomId) =>
      run((config, now) =>
        assignLoadsToRoom(
          config,
          loadsInRoom(config, roomId).map((load) => load.id),
          null,
          now,
        ),
      ),
    setDefaultRoom: (roomId) => run((config, now) => setDefaultRoom(config, roomId, now)),

    importConfig: (value) => {
      const decoded = decodeConfig(value, nowIso());
      if (decoded === null) {
        return fail(configIssue(ISSUE.IMPORT_UNREADABLE, { kind: ISSUE_TARGET.RCU }));
      }
      return get().applyConfig(decoded.config);
    },

    /**
     * A configuration that arrived from a file, not from a command.
     *
     * The command layer guarantees every commit is valid, so this is the one
     * door into the store that has to check for itself. It refuses rather than
     * throwing: a hand-edited file with a duplicate channel in it should leave
     * the tablet as it was and say why, not take the console down with it.
     */
    applyConfig: (config) => {
      const errors = errorsOf(validateConfig(config));
      const first = errors[0];
      if (first !== undefined) {
        return fail(
          configIssue(
            ISSUE.IMPORT_INVALID,
            { kind: first.target.kind, id: first.target.id },
            errors.length === 1
              ? stripPeriod(issueMessage(first))
              : `${stripPeriod(issueMessage(first))}, and ${errors.length - 1} more`,
          ),
        );
      }

      if (import.meta.env.DEV) assertConfigInvariants(config);
      set({ config });
      saveStoredConfig(config);
      return ok(config);
    },

    resetConfig: () => {
      // The key is removed rather than overwritten with an empty configuration:
      // "clear all data" should leave nothing behind, and the loader treats an
      // absent key as an empty configuration anyway.
      clearStoredConfig();
      set({ config: createEmptyConfig(nowIso()) });
    },
  };
});

export default useRcuConfigStore;
